import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  hasReachedAnalyticsMcpStep,
  isAnalyticsMcpPlaybackComplete,
  LAST_ANALYTICS_MCP_STEP,
  ANALYTICS_MCP_STEPS,
  analyticsMcpStepDelay,
  type AnalyticsMcpStep,
} from 'managed-analytics/domain/analyticsMcpPlayback'

const FIRST_STEP = ANALYTICS_MCP_STEPS[0]

type PlaybackOptions = {
  // Skips the reveal and shows the finished session. Used for reduced-motion
  // preferences, where an animated console is noise rather than explanation.
  instant?: boolean
}

/**
 * Drives the staged reveal of one rehearsed MCP session. Restarts whenever the
 * selected prompt changes, or when the visitor replays the same one.
 */
export function useAnalyticsMcpPlayback(promptId: string | undefined, options?: PlaybackOptions) {
  const instant = options?.instant ?? false

  const [step, setStep] = useState<AnalyticsMcpStep>(instant ? LAST_ANALYTICS_MCP_STEP : FIRST_STEP)
  // Bumping this re-runs the effect for an unchanged prompt, which is what
  // "Replay" means.
  const [runId, setRunId] = useState(0)

  useEffect(() => {
    if (!promptId) return

    if (instant) {
      setStep(LAST_ANALYTICS_MCP_STEP)
      return
    }

    setStep(FIRST_STEP)

    const timers = ANALYTICS_MCP_STEPS.slice(1).map(nextStep =>
      window.setTimeout(() => setStep(nextStep), analyticsMcpStepDelay(nextStep))
    )

    return () => timers.forEach(window.clearTimeout)
  }, [promptId, runId, instant])

  const replay = useCallback(() => setRunId(current => current + 1), [])

  const hasReached = useCallback(
    (candidate: AnalyticsMcpStep) => hasReachedAnalyticsMcpStep(step, candidate),
    [step]
  )

  return useMemo(
    () => ({
      step,
      hasReached,
      isComplete: isAnalyticsMcpPlaybackComplete(step),
      replay,
    }),
    [step, hasReached, replay]
  )
}
